import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import { showLazyLoadFailedToast } from './lazy-load-failed-toast'

/**
 * Handling for code that downloads on demand and fails to arrive. Every deploy replaces the hashed files in
 * `/assets`, so a tab opened before it 404s on the first lazy chunk it hasn't loaded yet (an offline device fails the
 * same way).
 *
 * - Route pages: TanStack Router's split components reload the page once on this error and land on the page; when
 *   they don't (it already did once for that message), the router's error fallback reloads, at most once a minute
 *   (`claimRouteChunkReload`, `components/layout/route-error-fallback.tsx`).
 * - In-page widgets (emoji picker, photo cropper, client drawers…): never reload on their own — the person may be
 *   mid-message. The widget renders nothing and a toast offers Refresh, so they choose when to lose the draft.
 * - A page's main body split out of its route (a Settings section): reloads once like a route page — nothing on it
 *   has loaded, so nothing typed can be lost — then falls back to the widget behaviour.
 */

/**
 * One shared download for a chunk that is both warmed up early (hover, a caller card appearing) and loaded on open.
 * A failed download is forgotten, so the next attempt retries.
 */
export function sharedImport<Module>(load: () => Promise<Module>): () => Promise<Module> {
  let pending: Promise<Module> | null = null
  return () => {
    pending ??= load().catch((error: unknown) => {
      pending = null
      throw error
    })
    return pending
  }
}

function RenderNothing() {
  return null
}

/**
 * `React.lazy` for one named export of an in-page widget. If the chunk can't load, the widget renders `unavailable`
 * (nothing by default, so the page keeps working) and the person gets the Refresh toast once. A dialog passes a
 * fallback that closes itself, so its owner is not left holding an open state nobody can see.
 */
export function lazyWidget<Props extends object, Name extends string>(
  load: () => Promise<Record<Name, ComponentType<Props>>>,
  name: Name,
  unavailable: ComponentType<Props> = RenderNothing as ComponentType<Props>
): LazyExoticComponent<ComponentType<Props>> {
  return lazy(() =>
    load().then(
      (module) => ({ default: module[name] }),
      (error: unknown) => {
        console.error(`Lazy widget "${name}" failed to load`, error)
        showLazyLoadFailedToast()
        return { default: unavailable }
      }
    )
  )
}

/** Set while a page-body reload is in flight, so a chunk still missing after it shows the toast instead of looping. */
const PAGE_BODY_RELOAD_KEY = 'hayven:lazy-page-body-reloaded'

function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeSession(key: string, value: string | null) {
  try {
    if (value === null) window.sessionStorage.removeItem(key)
    else window.sessionStorage.setItem(key, value)
  } catch {
    // Storage blocked (private mode): the toast path below still works.
  }
}

/**
 * True when an error means "this code file didn't download" rather than a bug in it: the browser's failed dynamic
 * import (Chrome / Firefox / Safari wording) or Vite's failed CSS preload for a chunk.
 */
export function isChunkLoadError(error: unknown): error is Error {
  if (!(error instanceof Error)) return false
  const { message } = error
  return (
    message.startsWith('Failed to fetch dynamically imported module') ||
    message.startsWith('error loading dynamically imported module') ||
    message.startsWith('Importing a module script failed') ||
    message.startsWith('Unable to preload CSS for')
  )
}

/** When the last automatic reload for a missing page chunk happened (ms since epoch). */
const ROUTE_CHUNK_RELOAD_AT_KEY = 'hayven:route-chunk-reloaded-at'

/**
 * A missing page chunk reloads automatically at most once in this window. Long enough that a chunk still missing
 * after the reload (offline, broken deploy) shows the Refresh notice instead of looping; short enough that the next
 * deploy, hours later in the same tab, reloads again.
 */
const ROUTE_CHUNK_RELOAD_WINDOW_MS = 60_000

/** True when a missing page chunk already reloaded the page less than a minute ago (read only). */
export function hasRecentRouteChunkReload(): boolean {
  const lastReloadAt = Number(readSession(ROUTE_CHUNK_RELOAD_AT_KEY))
  const sinceLastReload = Date.now() - lastReloadAt
  // A stamp in the future (clock set back) counts as none, so it can't block reloads until the clock catches up.
  return lastReloadAt > 0 && sinceLastReload >= 0 && sinceLastReload < ROUTE_CHUNK_RELOAD_WINDOW_MS
}

/**
 * Records an automatic reload for a missing page chunk, right before it happens; false when it mustn't happen (one
 * already ran within the minute, or storage is blocked so the reload couldn't be remembered and might repeat
 * forever). TanStack Router reloads once by itself, but remembers that per error message for the tab's whole life;
 * Safari's message names no file, so every deploy after the first would show an error page instead.
 */
export function claimRouteChunkReload(): boolean {
  if (hasRecentRouteChunkReload()) return false
  const now = String(Date.now())
  writeSession(ROUTE_CHUNK_RELOAD_AT_KEY, now)
  if (readSession(ROUTE_CHUNK_RELOAD_AT_KEY) !== now) return false
  claimedReloadAt = now
  return true
}

/** The stamp this page wrote when it claimed its reload; the page should be unloading from then on. */
let claimedReloadAt: string | null = null

/** A reload that hasn't replaced the page by then was cancelled (a leave-page prompt answered "Stay"). */
const ROUTE_CHUNK_RELOAD_STALL_MS = 10_000

/**
 * How much longer this page's reload for a missing chunk may still be under way (ms; 0 = none, or it was cancelled).
 * Counted from the claim, not from each error screen, so a screen mounted later waits only for what is left.
 */
export function routeChunkReloadStallRemainingMs(): number {
  if (claimedReloadAt === null || readSession(ROUTE_CHUNK_RELOAD_AT_KEY) !== claimedReloadAt) return 0
  const sinceClaim = Date.now() - Number(claimedReloadAt)
  // Clock set back since the claim: don't trust it to still be under way.
  if (sinceClaim < 0) return 0
  return Math.max(0, ROUTE_CHUNK_RELOAD_STALL_MS - sinceClaim)
}

/**
 * True while this page's reload for a missing chunk should still be under way. An error screen that mounts in the
 * moments before the page unloads (the router re-rendering the failed page) keeps the loading look instead of
 * flashing the notice; after the stall time it's no longer believed, so nothing waits on a cancelled reload.
 */
export function isRouteChunkReloadUnderWay(): boolean {
  return routeChunkReloadStallRemainingMs() > 0
}

/**
 * `React.lazy` for one named export that IS the page's content (not a widget on it). If the chunk can't load the page
 * reloads once, as a route page does; if it still can't, it renders nothing and the Refresh toast shows.
 */
export function lazyPageBody<Props extends object, Name extends string>(
  load: () => Promise<Record<Name, ComponentType<Props>>>,
  name: Name
): LazyExoticComponent<ComponentType<Props>> {
  return lazy(() =>
    load().then(
      (module) => {
        writeSession(PAGE_BODY_RELOAD_KEY, null)
        return { default: module[name] }
      },
      (error: unknown) => {
        console.error(`Lazy page body "${name}" failed to load`, error)
        if (readSession(PAGE_BODY_RELOAD_KEY) === null) {
          writeSession(PAGE_BODY_RELOAD_KEY, '1')
          window.location.reload()
          // Keep the fallback up until the reload replaces the page.
          return new Promise<never>(() => {})
        }
        showLazyLoadFailedToast()
        return { default: RenderNothing as ComponentType<Props> }
      }
    )
  )
}
